import { setCurrentMomentStatus } from 'shared/api/setCurrentMomentStatus'
import { setCurrentPin } from 'shared/api/setCurrentPin'
import { authActions } from '../slice/authDataSlice'

const statusAfterToggle = {
    accepted: 'wanted',
    wanted: 'accepted',
}

const statusAfterCycle = {
    accepted: 'rejected',
    wanted: 'accepted',
    rejected: 'wanted',
}

const errorPayload = (error) => ({
    code: error?.code || 'activity_error',
    message: 'Не удалось сохранить действие. Попробуйте ещё раз.',
})

const confirmedMomentPayload = (cardId, status, result) => {
    const payload = {
        cardId,
        status,
        occurredAt: result?.occurredAt ?? Date.now(),
    }
    if (result?.limitationCount !== undefined) {
        payload.limitationCount = result.limitationCount
    }
    if (result?.limitationTime !== undefined) {
        payload.limitationTime = result.limitationTime
    }
    return payload
}

const momentChoiceSnapshot = (state) => ({
    arrayCard: state.arrayCard,
    cards: state.cards,
    direction: state.direction,
    acceptedMoments: state.acceptedMoments,
    wantedMoments: state.wantedMoments,
    rejectedMoments: state.rejectedMoments,
    pinnedMoments: state.pinnedMoments,
    limitationCount: state.limitationCount,
    limitationTime: state.limitationTime,
})

const runActivityMutation =
    (mutation, confirmedAction) => async (dispatch, getState) => {
        if (getState().auth.activityPending) {
            return { applied: false, pending: true }
        }

        dispatch(authActions.activityStarted())
        try {
            const result = await mutation()
            dispatch(confirmedAction(result))
            return { applied: true, result }
        } catch (error) {
            console.error('Activity update failed', error)
            dispatch(authActions.activityFailed(errorPayload(error)))
            return { applied: false, error }
        } finally {
            dispatch(authActions.activityFinished())
        }
    }

const chooseMoment =
    (status, [card, direction]) =>
    async (dispatch, getState) => {
        if (card.id === 0) {
            dispatch(authActions.applyMomentChoice({ card, direction }))
            return { applied: true }
        }

        if (getState().auth.activityPending) {
            return { applied: false, pending: true }
        }

        const snapshot = momentChoiceSnapshot(getState().auth)
        dispatch(authActions.activityStarted())
        dispatch(
            authActions.applyMomentChoice({
                card,
                direction,
                status,
                occurredAt: Date.now(),
            })
        )

        try {
            const result = await setCurrentMomentStatus(card.id, status)
            dispatch(
                authActions.applyMomentStatus(
                    confirmedMomentPayload(card.id, status, result)
                )
            )
            return { applied: true, result }
        } catch (error) {
            console.error('Activity update failed', error)
            dispatch(authActions.restoreMomentChoice(snapshot))
            dispatch(authActions.activityFailed(errorPayload(error)))
            return { applied: false, error }
        } finally {
            dispatch(authActions.activityFinished())
        }
    }

const setMomentStatus = (cardId, status) => (dispatch) =>
    dispatch(
        runActivityMutation(
            () => setCurrentMomentStatus(cardId, status),
            (result) =>
                authActions.applyMomentStatus(
                    confirmedMomentPayload(Number(cardId), status, result)
                )
        )
    )

export const acceptMoment = (payload) => chooseMoment('accepted', payload)
export const wantMoment = (payload) => chooseMoment('wanted', payload)
export const rejectMoment = (payload) => chooseMoment('rejected', payload)

export const toggleMoment = ([currentStatus, cardId]) => {
    const nextStatus = statusAfterToggle[currentStatus]
    if (!nextStatus) {
        throw new TypeError('Moment status cannot be toggled')
    }
    return setMomentStatus(cardId, nextStatus)
}

export const cycleMoment = ([currentStatus, cardId]) => {
    const nextStatus = statusAfterCycle[currentStatus]
    if (!nextStatus) {
        throw new TypeError('Moment status cannot be cycled')
    }
    return setMomentStatus(cardId, nextStatus)
}

export const moveMomentToAccepted = (cardId) =>
    setMomentStatus(cardId, 'accepted')
export const moveMomentToWanted = (cardId) => setMomentStatus(cardId, 'wanted')
export const moveMomentToRejected = (cardId) =>
    setMomentStatus(cardId, 'rejected')

const setPin = (cardId, shouldPin) => (dispatch) =>
    dispatch(
        runActivityMutation(
            () => setCurrentPin(cardId, shouldPin),
            (result) =>
                authActions.applyPin({
                    cardId: Number(cardId),
                    shouldPin,
                    occurredAt: result?.occurredAt ?? Date.now(),
                })
        )
    )

export const addMomentPin = (cardId) => setPin(cardId, true)
export const removeMomentPin = (cardId) => setPin(cardId, false)
